import {
    InscriptionT,
    getFieldValueFromPhase,
} from 'routes/BudgetModule/shared/entities/inscription';
import { SecondaryBudgetAxisSources } from 'routes/Support/components/ClientSupport/components/BudgetConfiguration/types';
import _, { cloneDeep, pick, update } from 'lodash';
import {
    BudgetPhases,
    InscriptionStatusCategory,
    Phase,
} from 'business/constants';
import { translateBlockValue } from 'i18n';
import {
    BUDGET_LINE_INSCRIPTION_STEP,
    getEmptyPhaseColumns,
    COMMON_INSCRIPTION_COLUMNS_ORDER_FILTER_TO_EXPORT,
    EXTRA_COLUMNS_TO_EXPORT,
    INSCRIPTION_EXPENSE_INCOME,
    INSCRIPTION_INVESTMENT_OPERATING,
    LABEL_CIRIL_EXTRA_COLUMN,
    COMMON_INSCRIPION_COLUMNS_TO_EXPORT,
    PROJECT_ID,
    EXTRA_COLUMNS_FOR_REIMPORT,
    LABEL_CIRIL_EXTRA_COLUMN_REIMPORT,
    PHASE_STATUS,
    getPhaseInscriptionTableName,
    CSV_HEADER_CUSTOM_TRANSLATION,
    SECONDARY_BUDGET_AXIS_ELEMENT_CODE,
    OPERATION_TABLE,
    BUDGET_LINE_OPERATION_VENTILATION_CODE,
    SBA_LABEL_WITHOUT_CODE,
    createExtraColumn,
    ENVELOPPE_TYPE,
    BUDGET_LINE_PROGRAM_INTERNAL_ID,
    BUDGET_LINE_PROGRAM_SOFTWARE_ID,
} from 'routes/BudgetModule/config/constants';
import {
    cirilTranslationDataValue,
    jvsTranslationDataValue,
    seditTranslationDataValue,
} from 'routes/BudgetModule/helpers/format';
import { StatusesSettingsT } from 'routes/BudgetModule/config/types';
import { LINE_BREAK_REGEX } from 'helpers/exportHelpers';
import { useIntegrationSoftwares } from 'helpers/integrations';
import { TFunction, useTranslation } from 'react-i18next';
import { useDispatch } from 'store/hooks';
import { inscriptionHasStatus, isInscriptionVoted } from './statusHelpers';
import {
    getMultipleFileExcelExport,
    getPhaseSpecificColumns,
    getSingleFileExcelExport,
    groupInscriptionsByImputations,
    isLabelUndefined,
} from './exportHelpers';
import { Project } from '../shared/useGetProjects';
import { Dispatch } from 'store/store';
import { useGetPhases } from '../shared/phases/hook';
import { getCommentForPhase } from '../shared/phases/helpers';
import { getClientBind } from 'store/APIClient';

export type BudgetExcelExportCol = Pick<
    ExtraColumn,
    'key' | 'type' | 'field'
> & {
    label?: string;
};

/** Helper used when exporting inscriptions to excel format.
 * We need to add an extra footer line that is a normal budget line
 * but with 0 amount an no label, to workaround a ciril import bug
 * where the last line of the CSV is not taken into account in ciril...
 * So we add a useless line at the end!
 */
const getCommentColumnKeys = (
    selectedColsToExport: { key: string; type: string }[],
) =>
    selectedColsToExport
        .filter(
            (col) =>
                col.key.toLowerCase().endsWith('comment') ||
                col.key === LABEL_CIRIL_EXTRA_COLUMN.key ||
                // FIXME: it's hacky, it's ugly, I had to do it in a rush
                col.key === LABEL_CIRIL_EXTRA_COLUMN_REIMPORT.key,
        )
        .map((col) => col.key);

const getMoneyColumKeys = (
    selectedColsToExport: { key: string; type: string }[],
) =>
    selectedColsToExport
        .filter((col) => col.key.includes('amount'))
        .map((col) => col.key);

const addCIRILSheetFooter = ({
    selectedColsToExport,
    selectedDataToExport,
}: {
    selectedColsToExport: { key: string; type: string }[];
    selectedDataToExport: Partial<InscriptionT>[];
}) => {
    let footerLine = cloneDeep(selectedDataToExport.slice(-1)[0]);

    // comments and amounts from the new line are updated
    const commentColKeys = getCommentColumnKeys(selectedColsToExport);
    commentColKeys.forEach(
        (colKey) =>
            (footerLine = update(footerLine, colKey, () => 'FIN IMPORT')),
    );

    const moneyColKeys = getMoneyColumKeys(selectedColsToExport);
    moneyColKeys.forEach(
        (colKey) => (footerLine = update(footerLine, colKey, () => 0)),
    );

    return footerLine;
};

// Product requirement: libellé should be in uppercase, and should not contains new lines
// should we remove " too ?
// UPDATE: this is not done when grouped by imputations as we need a separator for comments
const getFixComment = (grouped: boolean) => {
    if (grouped) {
        return (str: string) => str && str.toUpperCase();
    }
    return (str: string) =>
        str && str.toUpperCase().replace(LINE_BREAK_REGEX, ' ');
};

const shouldKeepInscriptionForReimport = (
    inscription: InscriptionT,
    phase: BudgetPhases,
    statusesSettings: StatusesSettingsT,
) => {
    const isRejected = inscriptionHasStatus(
        inscription,
        phase,
        statusesSettings,
        InscriptionStatusCategory.REJECTED,
    );
    const isVoted = isInscriptionVoted(inscription, phase, statusesSettings);
    if (
        [
            BudgetPhases.ARBITRAGE,
            BudgetPhases.VOTE,
            BudgetPhases.SUPPLEMENTARY_BUDGET,
            BudgetPhases.ARBITRAGE_DM,
        ].includes(phase) &&
        isRejected
    )
        return false;
    if (phase === BudgetPhases.SUPPLEMENTARY_BUDGET && isVoted) return false;
    if (phase === BudgetPhases.ARBITRAGE_DM && isVoted) return false;

    // FIXME - custom AMP Shortcut ID : 31010
    if (inscription[PHASE_STATUS] === 'voted_primitive') {
        return false;
    }

    return true;
};

export const prepareInscriptionDataForExport = ({
    inscriptionsDataColumns,
    inscriptionsData,
    phase,
    statusesSettings,
    exportOptions,
    t,
    isReimport = false,
    secondaryBudgetAxisSourceTable,
}: {
    inscriptionsDataColumns: BudgetExcelExportCol[];
    inscriptionsData: InscriptionT[];
    phase: BudgetPhases;
    statusesSettings: StatusesSettingsT;
    exportOptions: ExportOptions;
    t: TFunction;
    isReimport?: boolean;
    secondaryBudgetAxisSourceTable?: SecondaryBudgetAxisSources | null;
}): {
    selectedColsToExport: BudgetExcelExportCol[];
    selectedDataToExport: InscriptionT[];
} => {
    const {
        inscriptionColumns,
        getPhaseColumns,
        formatValue,
        translateHeader,
        getFooter,
        extraColumns,
        filterSort,
        groupByImputations = false,
        filterForSoftware = undefined,
    } = exportOptions;

    // we fetch the columns to export for the current phase
    const phaseSpecificColumns = getPhaseColumns?.(phase) || [];
    const extraColumnsWithPhases: ExtraColumn[] = [];

    extraColumnsWithPhases.push(...(extraColumns || []));
    extraColumnsWithPhases.push(...(phaseSpecificColumns || []));

    // we filter the columns to export to keep only the ones that are in the columns of the inscriptions
    // FIXME : it's not clear why we need to do this here.
    // we also implicitely convert string[] inscriptionColumnsToExport into BudgetExcelExportCol[] within the translate method.
    // FIXME : this should be done within the parent method, in a dedicated "buildColumns" method
    let selectedColsToExport: BudgetExcelExportCol[] = _(
        inscriptionsDataColumns,
    )
        .map((col) => (translateHeader ? translateHeader(col) : col))
        .filter((col) => inscriptionColumns?.includes(col.key))
        .sortBy((col) => inscriptionColumns?.indexOf(col.key))
        .value();

    // temporary hack for product requirement: we want an empty STEP value
    // FIXME: the "temporary hack" lasts for 3 years now. This should be added in proper columns builder in parent.
    selectedColsToExport.push({
        key: BUDGET_LINE_INSCRIPTION_STEP,
        type: 'text',
    } as BudgetExcelExportCol);

    // we add extra columns
    // FIXME : the extraColumns could be "standard column" if we used a common column type and definition
    selectedColsToExport = selectedColsToExport.filter(
        (c) => !extraColumnsWithPhases.find((e) => e.key === c.key),
    );
    selectedColsToExport.push(
        ...extraColumnsWithPhases
            .map((col) => (translateHeader ? translateHeader(col) : col))
            .map(
                (e) =>
                    ({
                        label: isLabelUndefined(e.label)
                            ? t(`common.${e.field}`)
                            : e.label,
                        key: e.key,
                        type: 'text',
                    }) as const,
            ),
    );

    // we filter the inscriptions to keep only the ones that are in the current phase
    // we fetch values from inscriptions as defined by extraColumnsWithPhases
    let selectedDataToExport: Partial<InscriptionT>[] = inscriptionsData
        // if we're in reimport mode, we filter out the inscriptions that are rejected or voted depending on phase
        .filter((row: InscriptionT) => {
            if (!isReimport) return true;
            const shouldKeepForReimport = shouldKeepInscriptionForReimport(
                row,
                phase,
                statusesSettings,
            );
            if (!filterForSoftware) return shouldKeepForReimport;
            const shouldKeepForSpecificSoftware = filterForSoftware(row);
            return shouldKeepForReimport && shouldKeepForSpecificSoftware;
        })
        .map((row: InscriptionT) => {
            const data = {
                ...pick(row, inscriptionColumns),
                // temporary hack for product requirement: we want an empty STEP value
                // FIXME : this could be managed by a proper valueGetter in  a BUDGET_LINE_INSCRIPTION_STEP column definition
                [BUDGET_LINE_INSCRIPTION_STEP]: '',
            };
            const extraColumnsValues =
                extraColumnsWithPhases?.reduce(
                    (acc: { [key: string]: string | number }, c) => {
                        acc[c.key] = c.getValue(
                            row,
                            phase,
                            secondaryBudgetAxisSourceTable,
                        );
                        return acc;
                    },
                    {},
                ) ?? [];
            return {
                ...data,
                ...extraColumnsValues,
            };
        });

    // We group data by imputation : needed for some softwares
    if (groupByImputations) {
        selectedDataToExport = groupInscriptionsByImputations(
            // @ts-expect-error invalid type Partial<InscriptionT>[]
            selectedDataToExport,
            phase,
        );
    }

    // we filter and sort the columns according to filterSort function.
    // The main utility for post-filter is (probably) to allow groupByImputations (above) to work with columns that must be discarded in the end
    if (filterSort !== undefined) {
        selectedColsToExport = filterSort(selectedColsToExport);
        const colKeys = selectedColsToExport.map((c) => c.key);
        selectedDataToExport = selectedDataToExport.map((d) =>
            Object.fromEntries(
                Object.entries(d).filter(([key]) => colKeys.includes(key)),
            ),
        );
    }

    // For some columns (comments and status), we apply a specific formatting
    // Note : could be done with a proper getter in the columns definition
    const commentColKeys = getCommentColumnKeys(selectedColsToExport);
    const statusColKeys = selectedColsToExport
        .filter(
            (col) =>
                col.key.toLowerCase() === PHASE_STATUS ||
                col.key.toLowerCase().endsWith('.status'),
        )
        .map((col) => col.key);

    const formattedData = selectedDataToExport.map((item) => {
        let newItem = _.update(item, INSCRIPTION_EXPENSE_INCOME, formatValue);
        newItem = _.update(
            newItem,
            INSCRIPTION_INVESTMENT_OPERATING,
            formatValue,
        );

        // for status column, we translate the value using the doct in local/fr/sentence.json
        // this should probably be update in the future to use the backend translations
        statusColKeys.forEach(
            (colKey) =>
                (newItem = _.update(newItem, colKey, (value) =>
                    translateBlockValue(value),
                )),
        );
        commentColKeys.forEach(
            (colKey) =>
                (newItem = _.update(
                    newItem,
                    colKey,
                    getFixComment(groupByImputations),
                )),
        );
        return newItem;
    });

    // We add an optional footer line (only for CIRIL)
    const footerLine = getFooter
        ? getFooter({
              selectedColsToExport,
              selectedDataToExport,
          })
        : null;
    return {
        selectedColsToExport,
        selectedDataToExport:
            footerLine !== null
                ? [...formattedData, footerLine]
                : formattedData,
    };
};

const filterSort = (
    columns: BudgetExcelExportCol[],
    expected: string[],
): BudgetExcelExportCol[] => {
    const filteredColumns = columns.filter((c) => expected.includes(c.key));
    filteredColumns.sort(
        (a, b) => expected.indexOf(a.key) - expected.indexOf(b.key),
    );
    return filteredColumns;
};

const translateColHeader = (
    softwares: Set<string>,
    secondaryBudgetAxisSourceTable?: SecondaryBudgetAxisSources | null,
) => {
    const translations = Array.from(softwares).reduce(
        (acc: Record<string, string>, item) => {
            const softwareTrad =
                item in CSV_HEADER_CUSTOM_TRANSLATION
                    ? CSV_HEADER_CUSTOM_TRANSLATION[item]
                    : {};
            // FIXME: yes this is ugly, I'm hoping for a large overhaul in summer 2025
            if (item === 'GRAND_ANGLE' && secondaryBudgetAxisSourceTable) {
                const table =
                    secondaryBudgetAxisSourceTable === OPERATION_TABLE
                        ? 'Opération'
                        : 'Programme';
                softwareTrad[SECONDARY_BUDGET_AXIS_ELEMENT_CODE] =
                    `Code ${table}`;
                softwareTrad[SBA_LABEL_WITHOUT_CODE] = `Description ${table}`;
            }
            return { ...acc, ...softwareTrad };
        },
        {},
    );
    return (column: ResponseColumnT) => {
        const label =
            column.key in translations ? translations[column.key] : undefined;
        return isLabelUndefined(label) ? column : { ...column, label };
    };
};

export type ExtraColumn = {
    label: string | undefined;
    key: string;
    field?: string;
    getValue: (
        data?: InscriptionT,
        phase?: BudgetPhases,
        sourceTable?: SecondaryBudgetAxisSources | null,
    ) => string | number;
    type: ResponseColumnT['type'];
};

export type ExportOptions = {
    inscriptionColumns: string[];
    formatValue: (value: ResponseDataValueT) => ResponseDataValueT;
    translateHeader?: (value: BudgetExcelExportCol) => BudgetExcelExportCol;
    getFooter?: (arg: {
        selectedColsToExport: { key: string; type: string }[];
        selectedDataToExport: Partial<InscriptionT>[];
    }) => object;
    extraColumns?: ExtraColumn[];
    getPhaseColumns?: (phase: BudgetPhases) => ExtraColumn[];
    filterSort?: (columns: BudgetExcelExportCol[]) => BudgetExcelExportCol[];
    groupByImputations?: boolean;
    // name of the sheet in the excel file
    sheetName?: string;
    // extra sheets to add to the excel file, they will be empty
    extraSheets?: string[];
    // rows to append at the top of the excel file, used for Grand Angle reimport for instance
    extraRows?: (string | number | undefined)[][];
    // filter to apply on the inscriptions data before exporting
    filterForSoftware?: (inscription: InscriptionT) => boolean;
    fileFormat: 'xlsx' | 'xls';
};

export const getSoftwareExportOptions = ({
    softwares,
    currentPhase,
    extraColumns = [],
    isReimport = false,
    secondaryBudgetAxisSourceTable = null,
    exercice,
}: {
    softwares: Set<string>;
    currentPhase: BudgetPhases;
    extraColumns?: string[];
    isReimport?: boolean;
    secondaryBudgetAxisSourceTable?: SecondaryBudgetAxisSources | null;
    exercice: number;
}): ExportOptions => {
    // We consider the default export to be the CIRIL export
    const defaultExportOptions: ExportOptions = {
        inscriptionColumns:
            COMMON_INSCRIPION_COLUMNS_TO_EXPORT['CIRIL_FINANCES'],
        getPhaseColumns: (phase: BudgetPhases) =>
            getPhaseSpecificColumns(phase, ['amount']),
        formatValue: cirilTranslationDataValue,
        getFooter: addCIRILSheetFooter,
        fileFormat: 'xlsx',
    };

    const filterSortColumns = (
        filterColumns: string[],
        extraCols: string[] | undefined,
    ) => {
        return (columns: BudgetExcelExportCol[]) =>
            filterSort(columns, [...filterColumns, ...(extraCols || [])]);
    };

    if (softwares.has('CIRIL_FINANCES')) {
        return {
            ...defaultExportOptions,
            extraColumns: [
                ...EXTRA_COLUMNS_TO_EXPORT.CIRIL,
                ...(isReimport ? EXTRA_COLUMNS_FOR_REIMPORT.CIRIL : []),
            ],
            filterSort: filterSortColumns(
                COMMON_INSCRIPTION_COLUMNS_ORDER_FILTER_TO_EXPORT.CIRIL_FINANCES,
                extraColumns,
            ),
        };
    }
    if (softwares.has('SEDIT_FINANCES')) {
        return {
            inscriptionColumns:
                COMMON_INSCRIPION_COLUMNS_TO_EXPORT['SEDIT_FINANCES'],
            formatValue: seditTranslationDataValue,
            translateHeader: translateColHeader(softwares),
            groupByImputations: true,
            extraColumns: EXTRA_COLUMNS_TO_EXPORT.SEDIT_FINANCES,
            getPhaseColumns: (phase: BudgetPhases) =>
                getPhaseSpecificColumns(phase, ['amount', 'comment']),
            filterSort: filterSortColumns(
                COMMON_INSCRIPTION_COLUMNS_ORDER_FILTER_TO_EXPORT.SEDIT_FINANCES,
                extraColumns,
            ),
            fileFormat: 'xlsx',
        };
    }
    if (softwares.has('SEDIT_FINANCES_GFDE')) {
        return {
            inscriptionColumns:
                COMMON_INSCRIPION_COLUMNS_TO_EXPORT['SEDIT_FINANCES_GFDE'],
            formatValue: seditTranslationDataValue,
            translateHeader: translateColHeader(softwares),
            groupByImputations: true,
            extraColumns: EXTRA_COLUMNS_TO_EXPORT.SEDIT_FINANCES_GFDE,
            getPhaseColumns: (phase: BudgetPhases) =>
                getPhaseSpecificColumns(phase, ['amount']),
            filterSort: filterSortColumns(
                COMMON_INSCRIPTION_COLUMNS_ORDER_FILTER_TO_EXPORT.SEDIT_FINANCES_GFDE,
                extraColumns,
            ),
            fileFormat: 'xlsx',
        };
    }
    if (softwares.has('SIFAC')) {
        return {
            inscriptionColumns: COMMON_INSCRIPION_COLUMNS_TO_EXPORT['SIFAC'],
            formatValue: (value) => value,
            translateHeader: translateColHeader(softwares),
            extraColumns: EXTRA_COLUMNS_TO_EXPORT.SIFAC,
            getPhaseColumns: (phase: BudgetPhases) =>
                getPhaseSpecificColumns(phase, ['amount']),
            filterSort: filterSortColumns(
                COMMON_INSCRIPTION_COLUMNS_ORDER_FILTER_TO_EXPORT.SIFAC,
                extraColumns,
            ),
            groupByImputations: true,
            fileFormat: 'xlsx',
        };
    }
    if (softwares.has('ASTRE_GF')) {
        return {
            inscriptionColumns: COMMON_INSCRIPION_COLUMNS_TO_EXPORT['ASTRE_GF'],
            formatValue: (value) => value,
            translateHeader: translateColHeader(softwares),
            extraColumns: EXTRA_COLUMNS_TO_EXPORT.ASTRE_GF,
            getPhaseColumns: (phase: BudgetPhases) =>
                getPhaseSpecificColumns(phase, ['amount']),
            filterSort: filterSortColumns(
                COMMON_INSCRIPTION_COLUMNS_ORDER_FILTER_TO_EXPORT.ASTRE_GF,
                extraColumns,
            ),
            groupByImputations: true,
            fileFormat: 'xlsx',
        };
    }
    if (softwares.has('JVS_FINANCES')) {
        return {
            inscriptionColumns:
                COMMON_INSCRIPION_COLUMNS_TO_EXPORT['JVS_FINANCES'],
            formatValue: jvsTranslationDataValue,
            extraColumns: [
                ...EXTRA_COLUMNS_TO_EXPORT.JVS_FINANCES,
                ...getEmptyPhaseColumns('JVS_FINANCES', currentPhase),
            ],
            getPhaseColumns: (phase: BudgetPhases) =>
                getPhaseSpecificColumns(phase, ['amount', 'comment']),
            translateHeader: translateColHeader(softwares),
            filterSort: filterSortColumns(
                COMMON_INSCRIPTION_COLUMNS_ORDER_FILTER_TO_EXPORT.JVS_FINANCES,
                extraColumns,
            ),
            sheetName: 'Préparation du budget',
            extraSheets: ['Identifiant simul'],
            groupByImputations: true,
            fileFormat: 'xlsx',
        };
    }
    if (softwares.has('GRAND_ANGLE')) {
        const tableName =
            secondaryBudgetAxisSourceTable === OPERATION_TABLE
                ? 'Operation'
                : 'Programme';
        const extraRows = isReimport
            ? [
                  ['', '', "Exercice d'extraction : ", '', exercice.toString()],
                  ['', 1, "Exercice d'import : ", '', exercice.toString()],
                  ['', tableName],
                  [''],
              ]
            : [];
        const bind = getClientBind();
        const shouldRemoveOperationVentilationCode = bind !== 'deux-sevres';
        const orderedColumns =
            COMMON_INSCRIPTION_COLUMNS_ORDER_FILTER_TO_EXPORT.GRAND_ANGLE;
        const filteredColumns = shouldRemoveOperationVentilationCode
            ? orderedColumns.filter(
                  (col) => col !== BUDGET_LINE_OPERATION_VENTILATION_CODE,
              )
            : orderedColumns;
        const grandAngleExtraColumns = EXTRA_COLUMNS_TO_EXPORT.GRAND_ANGLE;
        // For Grand Angle versions with Opérations as secondary budget axis source table,
        // we add the budget line program internal id as an extra column.
        const programCol = createExtraColumn({
            key: BUDGET_LINE_PROGRAM_INTERNAL_ID,
            getValue: (data: InscriptionT) =>
                `${data[BUDGET_LINE_PROGRAM_SOFTWARE_ID]?.split('@@')?.[1]}`,
        });
        const shouldAddProgramColumn =
            isReimport && secondaryBudgetAxisSourceTable === OPERATION_TABLE;
        return {
            inscriptionColumns:
                COMMON_INSCRIPION_COLUMNS_TO_EXPORT['GRAND_ANGLE'],
            formatValue: (value) => value,
            getPhaseColumns: (phase: BudgetPhases) =>
                getPhaseSpecificColumns(phase, ['amount', 'comment']),
            translateHeader: translateColHeader(
                softwares,
                secondaryBudgetAxisSourceTable,
            ),
            extraColumns: shouldAddProgramColumn
                ? [...grandAngleExtraColumns, programCol]
                : grandAngleExtraColumns,
            filterSort: filterSortColumns(filteredColumns, extraColumns),
            groupByImputations: true,
            extraRows,
            filterForSoftware: (inscription: InscriptionT) => {
                const enveloppeType = inscription[ENVELOPPE_TYPE];

                if (!enveloppeType) return true;
                // Grand Angle disallows reimporting AP or AE inscriptions for now
                return ['EPI', 'EPF'].includes(enveloppeType);
            },
            fileFormat: 'xlsx',
            ...(isReimport
                ? { sheetName: 'Prévisions Budgétaires', fileFormat: 'xls' }
                : {}),
        };
    }
    return defaultExportOptions;
};

const getCustomExportInscriptionsExcel = ({
    inscriptionsData,
    phase,
    statusesSettings,
    softwares,
    exportOptions,
    trackEventName,
    trackingMetadata,
    t,
    dispatch,
    isReimport = false,
    secondaryBudgetAxisSourceTable,
}: {
    inscriptionsData: { columns: BudgetExcelExportCol[]; data: InscriptionT[] };
    phase: BudgetPhases;
    statusesSettings: StatusesSettingsT;
    softwares: Set<string>;
    exportOptions: ExportOptions;
    trackEventName?: string;
    trackingMetadata?: {
        budgetProposal: string;
        phase: string;
    };
    t: TFunction;
    dispatch: Dispatch;
    isReimport?: boolean;
    secondaryBudgetAxisSourceTable?: SecondaryBudgetAxisSources | null;
}): (() => void) => {
    const args = {
        inscriptionsData,
        phase,
        statusesSettings,
        softwares,
        exportOptions,
        trackEventName,
        trackingMetadata,
        t,
        dispatch,
        isReimport,
        secondaryBudgetAxisSourceTable,
    };
    return softwares.has('GRAND_ANGLE') && isReimport
        ? getMultipleFileExcelExport(args)
        : getSingleFileExcelExport(args);
};

export const useExportInscriptionsExcelForReimport = ({
    inscriptionsData,
    phase,
    statusesSettings,
    trackEventName,
    trackingMetadata,
    secondaryBudgetAxisSourceTable,
    exercice,
}: {
    inscriptionsData: { columns: BudgetExcelExportCol[]; data: InscriptionT[] };
    phase: BudgetPhases;
    statusesSettings: StatusesSettingsT;
    trackEventName?: string;
    trackingMetadata?: {
        budgetProposal: string;
        phase: string;
    };
    secondaryBudgetAxisSourceTable: SecondaryBudgetAxisSources | null;
    exercice: number;
}) => {
    const { data: softwares } = useIntegrationSoftwares();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    if (softwares === undefined) {
        return () => {};
    }
    const exportOptions = getSoftwareExportOptions({
        softwares,
        currentPhase: phase,
        isReimport: true,
        exercice,
        secondaryBudgetAxisSourceTable,
    });

    return getCustomExportInscriptionsExcel({
        inscriptionsData,
        phase,
        statusesSettings,
        softwares,
        exportOptions,
        trackEventName,
        trackingMetadata,
        t,
        dispatch,
        isReimport: true,
        secondaryBudgetAxisSourceTable,
    });
};

export const getInscriptionExtraColumnsForExport = (
    phase: BudgetPhases,
    phasesSettings: Phase[],
    projects: Project[],
    softwares: Set<string>,
    t: TFunction<'translation'>,
): Map<string, ExtraColumn> => {
    const result = new Map<string, ExtraColumn>();
    const statusField = `${getPhaseInscriptionTableName(phase)}.status`;
    const commentField = getCommentForPhase(phase);

    result.set(statusField, {
        label: t('common.status'),
        type: 'text',
        key: statusField,
        getValue: (data: InscriptionT) =>
            (getFieldValueFromPhase(phase, 'status', data) as string) ?? '',
    });

    if (commentField && !softwares.has('CIRIL_FINANCES')) {
        result.set(commentField, {
            label: t('common.label'),
            type: 'text',
            key: commentField,
            getValue: (data: InscriptionT) =>
                (getFieldValueFromPhase(phase, 'comment', data) as string) ??
                '',
        });
    }

    result.set(PROJECT_ID, {
        label: t('budgetProposal.projects'),
        type: 'text',
        key: PROJECT_ID,
        getValue: (data: InscriptionT) => {
            if (!data[PROJECT_ID]) {
                return '';
            }
            return data[PROJECT_ID].map((projectId: number) => {
                const project = projects.find((p) => p.id === projectId);
                return project?.name;
            })
                .filter((p) => p)
                .join(', ');
        },
    });

    return result;
};

export const useExportInscriptionsExcel = ({
    inscriptionsData,
    phase,
    statusesSettings,
    projects,
    trackEventName,
    trackingMetadata,
    exercice,
}: {
    inscriptionsData: { columns: BudgetExcelExportCol[]; data: InscriptionT[] };
    phase: BudgetPhases;
    statusesSettings: StatusesSettingsT;
    projects: Project[];
    trackEventName?: string;
    trackingMetadata?: {
        budgetProposal: string;
        phase: string;
    };
    exercice: number;
}) => {
    const { t } = useTranslation();
    const { data: phasesSettings = [] } = useGetPhases();
    const dispatch = useDispatch();
    const { data: softwares } = useIntegrationSoftwares();
    if (softwares === undefined) {
        return () => {};
    }

    const cols = getInscriptionExtraColumnsForExport(
        phase,
        phasesSettings,
        projects,
        softwares,
        t,
    );
    const exportOptions = getSoftwareExportOptions({
        softwares,
        currentPhase: phase,
        extraColumns: Array.from(cols.keys()),
        exercice,
    });

    exportOptions.groupByImputations = false;

    return getCustomExportInscriptionsExcel({
        inscriptionsData,
        phase,
        statusesSettings,
        softwares,
        exportOptions: {
            ...exportOptions,
            extraColumns: [
                ...(exportOptions.extraColumns ?? []),
                ...Array.from(cols.values()),
            ],
        },
        trackEventName,
        trackingMetadata,
        t,
        dispatch,
    });
};
